import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink, ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import Header from './components/Header';
import Home from './pages/Home';
import CandidateSearch from './pages/CandidateSearch';
import AdminPanel from './pages/AdminPanel';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Help from './pages/Help';
import MyConnections from './pages/MyConnections';
import Profile from './pages/Profile';
import Careers from './pages/Careers';
import About from './pages/About';
import Feed from './feed/pages/Feed';
import SinglePost from './feed/pages/SinglePost';
import Notifications from './feed/pages/Notifications';
import Init from './pages/Init';
import useAuthStore, { setApolloClient } from './stores/authStore';
import './App.css';

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URI || 'http://localhost:8000/graphql',
});

// Add auth token and Turnstile token to requests
const authLink = setContext((_, { headers }) => {
  const token = useAuthStore.getState().token;
  const turnstileToken = sessionStorage.getItem('turnstile-token');
  
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
      'cf-turnstile-token': turnstileToken || "",
    }
  }
});

// Intercept responses to check for new token in headers
const afterwareLink = new ApolloLink((operation, forward) => {
  return forward(operation).map(response => {
    const context = operation.getContext();
    const newToken = context.response.headers.get('x-new-token');
    
    if (newToken) {
      // Update token in Zustand store
      useAuthStore.getState().updateToken(newToken);
    }
    
    return response;
  });
});

// Handle authentication and CAPTCHA errors
const errorLink = onError(({ graphQLErrors, networkError }) => {
  const turnstileEnabled = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;
  
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      if (extensions?.code === 'UNAUTHENTICATED' || message.includes('authentication')) {
        // Clear auth state and redirect to signin
        useAuthStore.getState().signout();
        window.location.href = '/signin';
      }
      if (turnstileEnabled && message.includes('CAPTCHA')) {
        // Clear Turnstile token and reload to show CAPTCHA again
        sessionStorage.removeItem('turnstile-token');
        window.location.reload();
      }
    });
  }
  if (networkError) {
    if (networkError.statusCode === 401) {
      useAuthStore.getState().signout();
      window.location.href = '/signin';
    }
    if (turnstileEnabled && networkError.statusCode === 403) {
      // CAPTCHA verification failed - clear token and reload
      sessionStorage.removeItem('turnstile-token');
      window.location.reload();
    }
  }
});

const client = new ApolloClient({
  link: errorLink.concat(authLink.concat(afterwareLink.concat(httpLink))),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all'
    },
    query: {
      errorPolicy: 'all'
    }
  }
});

// Register Apollo client with auth store for cache clearing on signout
setApolloClient(client);

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('darkMode');
    return savedTheme ? JSON.parse(savedTheme) : false;
  });

  const [vpnChecked, setVpnChecked] = useState(false);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // VPN check on app load
  useEffect(() => {
    const checkVPN = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/signin`, {
          method: 'HEAD',
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        if (response.status === 403) {
          // VPN detected - redirect to Y Combinator
          window.location.href = 'https://www.ycombinator.com';
        } else {
          // No VPN detected, allow access
          setVpnChecked(true);
        }
      } catch (error) {
        console.error('VPN check error:', error);
        // On error, allow access
        setVpnChecked(true);
      }
    };

    checkVPN();
  }, []);

  // Show Init page while VPN check is in progress
  if (!vpnChecked) {
    return <Init />;
  }

  return (
    <ApolloProvider client={client}>
      <Router>
        <div className={`App ${darkMode ? 'dark' : 'light'}`}>
          <Routes>
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route
              path="/*"
              element={
                <>
                  <Header darkMode={darkMode} setDarkMode={setDarkMode} />
                  <main>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/search" element={<CandidateSearch />} />
                      <Route path="/connections" element={<MyConnections />} />
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/feed" element={<Feed />} />
                      <Route path="/feed/:postId" element={<SinglePost />} />
                      <Route path="/notifications" element={<Notifications />} />
                      <Route path="/admin" element={<AdminPanel />} />
                      <Route path="/help" element={<Help />} />
                      <Route path="/careers" element={<Careers />} />
                      <Route path="/about" element={<About />} />
                    </Routes>
                  </main>
                </>
              }
            />
          </Routes>
        </div>
      </Router>
    </ApolloProvider>
  );
}

export default App;
